<template>
  <div class="edit-url-container" data-testid="edit-url-modal">
    <div class="modal-header">
      <h2 class="modal-title">Edit request video link</h2>
    </div>

    <FormKit type="form" @submit="handleSubmit" :actions="false" v-slot="{ state }">
      <div class="form-group">
        <label class="form-label">Current URL</label>
        <div class="url-preview" data-testid="url-preview">{{ urlPreview }}</div>
      </div>

      <div class="form-group">
        <label class="form-label">Custom URL</label>
        <FormKit
          type="text"
          name="customUrl"
          v-model="customUrl"
          :placeholder="originalUrl"
          validation="required|length:5,50|matches:/^[a-zA-Z0-9-_]*$/|checkUrlAvailability"
          validation-visibility="live"
          :validation-messages="{
            required: 'Custom URL is required',
            length: 'Custom URL must be 5-50 characters long',
            matches: 'Only letters, numbers, hyphens, and underscores allowed',
            checkUrlAvailability: 'This URL is already taken',
          }"
          :validation-rules="{ checkUrlAvailability }"
          ref="urlInput"
          class="url-input"
          data-testid="custom-url-input"
        />
      </div>

      <p class="security-warning" data-testid="security-warning">
        Short request links can be easily guessed, which may result in receiving harmful or distracting content.
      </p>

      <div class="form-actions">
        <Button
          type="solid"
          variant="blue"
          text="Update request link"
          :disabled="!state.valid || !state.dirty"
          @click="handleClick(state)"
          data-testid="update-url-button"
        />
        <Button type="ghost" variant="black" text="Cancel" @click="$emit('close')" data-testid="edit-url-cancel-button" />
      </div>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/storybook';
import { FormKit } from '@formkit/vue';
import { useToast } from '@/composables/useToast';
import { useDebug, DEBUG_NAMESPACE } from '@/composables/useDebug';
import type { RequestContent } from '../../types';
import { useRequestContentEditUrl } from '../../composables/useRequestContentEditUrl';
import { useRequestContents } from '../../composables/useRequestContents';

interface Props {
  item: RequestContent;
  domain: string;
}

const props = defineProps<Props>();

interface Emits {
  close: [];
  save: [item: RequestContent];
}

const emit = defineEmits<Emits>();
const { success, error } = useToast();
const { checkCustomUrlAvailability } = useRequestContents();
const { debug } = useDebug(DEBUG_NAMESPACE.STORE_REQUEST_CONTENT);

// Use composable for edit URL functionality
const { customUrl, urlInput, originalUrl, urlPreview, handleSubmit } = useRequestContentEditUrl({
  item: props.item,
  domain: props.domain,
});

// Async validation function for custom URL availability
const checkUrlAvailability = async (node: any) => {
  const value = node.value;
  if (!value || value.length < 5) return true; // Skip validation for empty or too short values

  try {
    const isAvailable = await checkCustomUrlAvailability(value, props.item.id);
    return isAvailable;
  } catch (error) {
    debug('Error checking URL availability:', error);
    return false; // Assume URL is not available on error
  }
};

const handleClick = async (formState: any) => {
  try {
    const updatedItem = await handleSubmit(formState);

    if (updatedItem) {
      success('Request link URL updated successfully');
      emit('save', updatedItem);
    } else {
      emit('close');
    }
  } catch (apiError: any) {
    debug('Failed to update URL - Full error:', apiError);

    // Extract the proper error message from the API response
    let errorMessage = 'Failed to update request link URL. Please try again.';

    if (apiError?.response?.data?.errors && Array.isArray(apiError.response.data.errors)) {
      // Get the first error detail from the backend validation
      const firstError = apiError.response.data.errors[0];
      if (firstError?.detail) {
        errorMessage = firstError.detail;
      }
    }

    error(errorMessage);
  }
};
</script>

<style scoped>
.edit-url-container {
  @apply flex flex-col gap-4 p-0 min-h-0 w-full h-full;
}

.form-group {
  @apply mb-6;
}

.form-label {
  @apply block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2;
}

.url-preview {
  @apply mb-2 text-sm text-zinc-600 dark:text-zinc-400 font-mono
         bg-zinc-50 dark:bg-zinc-800 p-2 rounded border
         border-zinc-200 dark:border-zinc-700;
}

.url-input {
  @apply w-full;
}

.security-warning {
  @apply text-sm text-amber-600 dark:text-amber-400
         bg-amber-50 dark:bg-amber-900/20 p-3 rounded
         border border-amber-200 dark:border-amber-800;
}

.form-actions {
  @apply flex gap-3 pt-4 mt-4 border-t border-zinc-200 dark:border-zinc-700;
}

.modal-header {
  @apply flex items-center justify-between mb-6 border-b border-zinc-200 dark:border-zinc-700 pb-4;
}

.modal-title {
  @apply text-xl font-semibold text-zinc-900 dark:text-zinc-100;
}
</style>
